import { Component, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from '@angular/material/input';
import { CONSTANTS } from '../../../../Constants/Constants';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ISearchOpportunityData } from '../../../../models/common/ISearchOpportunityData';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { CRMApiService } from '../../../../services/api/crm-api.service';
import { Subject, takeUntil } from 'rxjs';
import { OpportunityModel, SearchOpportunityRequest } from '../../../../models/dtos';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';

@Component({
  selector: 'app-search-opportunity-dialog',
  imports: [
    TranslatePipe,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatTableModule,
    CloseOnEscapeDirective
  ],
  templateUrl: './search-opportunity-dialog.component.html',
  styleUrl: './search-opportunity-dialog.component.scss'
})
export class SearchOpportunityDialogComponent implements OnDestroy {
  searchOpportunityForm: FormGroup<{
    id: FormControl<string>;
    sapJobNumber: FormControl<string>;
    name: FormControl<string>;
  }> = new FormGroup({
    id: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    sapJobNumber: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    name: new FormControl<string>(CONSTANTS.EMPTY_STRING)
  });

  private readonly destroy$ = new Subject<void>();
  dataList: ISearchOpportunityData[] = [];


  dataSource = new MatTableDataSource<ISearchOpportunityData>([]);

  // Table configuration
  displayedColumns: string[] = ['opportunityId', 'opportunityName', 'opportunityDescription', 'sapJobNumber', 'soldToSAPNumber', 'buyAmerican', 'restrictedType'];
  selectedRow: ISearchOpportunityData | null = null;

  constructor(
    private readonly notificationService: NotificationMessageService,
    private readonly crmApiService: CRMApiService,
    private readonly dialogRef: MatDialogRef<SearchOpportunityDialogComponent>
  ) { }

  resetForm() {
    this.searchOpportunityForm.reset();
  }

  get formControls() {
    return this.searchOpportunityForm.controls;
  }

  onRowClick(row: ISearchOpportunityData): void {
    this.selectedRow = row;
  }

  searchOpportunity(): void {
    const formValue = this.searchOpportunityForm.value;

    if (!formValue.id && !formValue.sapJobNumber && !formValue.name) {
      this.notificationService.showNotificationMessageDialog(
        CONSTANTS.TYPE_ERROR,
        'ERROR_TITLE',
        'PLEASE_ENTER_SEARCH_TEXT',
        CONSTANTS.DEFAULT_DIALOG_WIDTH
      );
      return;
    }

    const requestBody: SearchOpportunityRequest = {
      opportunityKey: formValue.id || null,
      opportunityName: formValue.name || null,
      sapJobNumber: formValue.sapJobNumber || null,
      cultureId: 1
    };

    this.crmApiService.searchOpportunities(requestBody).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: OpportunityModel[]) => {
        if (response && response.length > CONSTANTS.ZERO) {
          this.showSearchOpportunityResults(response);
        }
      },
      error: (error) => {
        console.error('Error searching opportunities');
      }
    });
  }

  showSearchOpportunityResults(opportunities: OpportunityModel[]): void {
    this.dataList = [];
    opportunities.forEach(opp => {
      const opportunityData: ISearchOpportunityData = {
        opportunityId: opp.opportunityId,
        opportunityName: opp.opportunityName,
        opportunityDescription: opp.opportunityDescription,
        sapJobNumber: opp.stSAPJobNumber,
        soldToSAPNumber: opp.soldToSAPNumber,
        buyAmerican: opp.buyAmerican,
        restrictedType: opp.isRestrictedOpportunity === 'Yes' ? 'Restricted' : 'Unrestricted'
      };

      this.dataList.push(opportunityData);
    });

    this.dataSource.data = this.dataList;
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  opportunityRowSelected(): void {
    if (this.selectedRow) {
      this.dialogRef.close(this.selectedRow.opportunityId);
    }
  }

  onDblClick(row: ISearchOpportunityData): void {
    this.selectedRow = row;
    this.opportunityRowSelected();
  }
}
